// Contenu pédagogique "façon formateur" pour le parcours de formation.
// Chaque module = des diapositives d'enseignement + une pratique.
// `practiceIds` : questions dont le contenu est EXPLICITEMENT enseigné (aucune question hors-programme).

export const LESSONS = {
  basic: {
    "B-001": {
      tagline: "Comprendre le cadre légal qui encadre la radio amateur au Canada.",
      practiceIds: ["B-001-001-001", "B-001-001-003", "B-001-002-002", "B-001-002-001"],
      slides: [
        {
          title: "La hiérarchie des règles",
          body: "Bienvenue ! Avant de toucher un émetteur, il faut connaître le cadre légal. Tout part de la **Loi sur la radiocommunication**, adoptée par le Parlement : c'est elle qui donne le pouvoir d'établir les règlements. En dessous viennent le **Règlement sur la radiocommunication** (qui définit notamment le « service de radioamateur ») puis les **Normes d'exploitation** (les détails techniques : bandes, puissances, etc.).",
          points: [
            "Loi sur la radiocommunication = la source de tout pouvoir de réglementer.",
            "Règlement sur la radiocommunication = les règles d'application.",
            "Normes d'exploitation (RIC) = les détails techniques concrets.",
          ],
          tip: "Loi → Règlement → Normes : du général au particulier.",
        },
        {
          title: "ISDE, l'autorité de tutelle",
          body: "Le ministère **Innovation, Sciences et Développement économique Canada (ISDE)** administre la Loi : il fait passer les examens, attribue les **indicatifs d'appel**, publie les documents (les « RIC ») et intervient en cas de brouillage. Le spectre des fréquences est une ressource publique limitée : ISDE en organise le partage équitable entre tous les services (aviation, marine, radioamateurs, etc.).",
          points: [
            "ISDE administre la Loi et délivre les certificats.",
            "Le spectre est une ressource publique partagée.",
            "Les documents de référence s'appellent les « RIC ».",
          ],
          tip: "En cas de doute réglementaire, la référence officielle vient d'ISDE.",
        },
        {
          title: "Certificat et indicatif",
          body: "Après l'examen, vous recevez un **Certificat d'opérateur radio** valide **à vie** (aucun renouvellement). On vous attribue un **indicatif d'appel** unique (ex. VE2ABC) que vous devez transmettre pour vous identifier : au début et à la fin d'un contact, et au moins toutes les 30 minutes. Vous devez tenir vos coordonnées à jour : tout **changement d'adresse** doit être communiqué à **ISDE**.",
          points: [
            "Certificat valide à vie, à conserver à la station.",
            "Changement d'adresse → prévenir ISDE avec les détails.",
            "Identification régulière avec votre indicatif.",
          ],
          tip: "Le préfixe de l'indicatif (VA, VE, VO, VY…) indique la région canadienne.",
        },
        {
          title: "Ce qui est permis… et interdit",
          body: "La radio amateur sert à l'**auto-formation technique**, à l'**expérimentation** et aux communications personnelles **non commerciales**. Sont interdits : les communications commerciales rémunérées, la diffusion de musique, le langage grossier, le **brouillage volontaire** et le **chiffrement** destiné à masquer le sens d'un message. En situation d'urgence mettant des vies en danger, toutes les restrictions s'effacent : la priorité absolue est le secours.",
          points: [
            "But : technique, expérimentation, entraide — jamais le commerce.",
            "Interdit : musique, grossièretés, brouillage, chiffrement du sens.",
            "Urgence vitale : toutes les règles cèdent devant le secours.",
          ],
          tip: "Aider en cas de détresse est non seulement permis, c'est prioritaire.",
        },
        {
          title: "Fiche mémo — Réglementation",
          body: "Récapitulatif des points de droit à retenir par cœur pour l'examen : ils reviennent presque systématiquement.",
          points: [
            "Hiérarchie : **Loi** → **Règlement** → **Normes (RIC)**.",
            "Certificat d'opérateur **valide à vie**, sans renouvellement.",
            "Identification : **au début**, **à la fin** d'un contact et **au moins toutes les 30 minutes**.",
            "Tout **changement d'adresse** doit être signalé à **ISDE**.",
            "Interdits : **commerce**, **musique**, **grossièretés**, **chiffrement** du sens, **brouillage volontaire**.",
            "**Urgence vitale** : toutes les restrictions cèdent devant le secours.",
          ],
          tip: "Si une question porte sur « qui/quand/quoi est permis », revenez à cette fiche.",
        },
      ],
    },
    "B-002": {
      tagline: "Savoir dialoguer proprement et efficacement sur les ondes.",
      practiceIds: ["B-002-001-002", "B-002-001-001", "B-002-001-011", "B-002-002-001"],
      slides: [
        {
          title: "Simplex, duplex et répéteurs",
          body: "En **simplex**, les deux stations émettent et reçoivent sur la **même fréquence**, chacune son tour : c'est le contact direct. Un **répéteur** fonctionne en **duplex** : il écoute sur une fréquence et retransmet simultanément sur une autre, depuis un point haut, ce qui **améliore fortement la portée** des portatifs et mobiles en VHF/UHF.",
          points: [
            "Simplex : une seule fréquence, contact direct.",
            "But du répéteur : améliorer la portée des petites stations.",
            "Le répéteur émet et reçoit en même temps (duplex).",
          ],
          tip: "Loin ou en zone urbaine dense ? Le répéteur est votre allié.",
        },
        {
          title: "Décalage et tonalité d'accès",
          body: "Comme il émet et reçoit en même temps, le répéteur utilise deux fréquences séparées par un **décalage (offset)**. Sur la bande **2 mètres**, ce décalage est normalement de **600 kHz**. Beaucoup de répéteurs exigent en plus une **tonalité sous-audible (CTCSS)** pour s'ouvrir, ce qui les protège du bruit et des signaux indésirables.",
          points: [
            "Décalage standard sur 2 m : 600 kHz.",
            "CTCSS : tonalité qui « ouvre » le répéteur.",
            "Votre appareil gère automatiquement l'émission/réception décalées.",
          ],
          tip: "Pas de réponse via un répéteur ? Vérifiez le décalage et la tonalité CTCSS.",
        },
        {
          title: "Établir un contact",
          body: "Pour appeler une station précise, on donne d'abord **l'indicatif de la station visée, puis le sien**. Pour un appel général, on lance **« CQ »**. On laisse toujours une **courte pause** entre les transmissions : quelqu'un pourrait avoir un message urgent à passer. Les **codes Q** (QTH = position, QSO = contact, QSY = changer de fréquence) abrègent les échanges.",
          points: [
            "Contact ciblé : indicatif visé, puis le vôtre.",
            "« CQ » = appel général à tout correspondant.",
            "Laissez une pause pour les urgences éventuelles.",
          ],
          tip: "Écoutez avant d'émettre : ne coupez jamais un QSO en cours.",
        },
        {
          title: "L'alphabet phonétique",
          body: "Pour être compris malgré le bruit et le fading, on épelle avec l'**alphabet phonétique international** : A = Alfa, B = Bravo, C = Charlie, D = Delta… C'est une norme mondiale (aussi utilisée en aviation) qui lève les confusions entre lettres proches à l'oreille (B/P, M/N).",
          points: [
            "Épelez systématiquement votre indicatif en phonétique.",
            "Norme internationale : Alfa, Bravo, Charlie, Delta, Echo…",
            "Objectif : zéro ambiguïté, même avec un signal faible.",
          ],
          tip: "Un opérateur clair et poli se fait toujours mieux comprendre.",
        },
        {
          title: "Fiche mémo — Procédures d'exploitation",
          body: "Les réflexes de communication et les chiffres à connaître pour dialoguer proprement.",
          points: [
            "**Simplex** = une seule fréquence ; **répéteur** = **duplex** (émet et reçoit en même temps).",
            "Décalage (offset) standard sur la bande **2 m : 600 kHz**.",
            "**CTCSS** = tonalité sous-audible qui « ouvre » le répéteur.",
            "**CQ** = appel général ; contact ciblé = indicatif visé **puis** le vôtre.",
            "Épelez toujours en **alphabet phonétique** (Alfa, Bravo, Charlie…).",
            "Laissez une **courte pause** entre les transmissions (urgences possibles).",
          ],
          tip: "Décalage 600 kHz + tonalité CTCSS : le duo qui règle 90 % des problèmes de répéteur.",
        },
      ],
    },
    "B-003": {
      tagline: "Assembler une station HF efficace et parfaitement sûre.",
      practiceIds: ["B-003-001-001", "B-003-001-003", "B-003-001-004", "B-003-001-007"],
      slides: [
        {
          title: "Le filtre passe-bas",
          body: "Tout émetteur produit, en plus du signal utile, des **fréquences harmoniques** (multiples 2×, 3×… de la fréquence). Non filtrées, elles brouillent la télévision et d'autres services. Le **filtre passe-bas** laisse passer votre signal HF et **bloque les harmoniques** ; il est le plus efficace **raccordé au plus près de la sortie de l'émetteur-récepteur**.",
          points: [
            "Harmoniques = copies indésirables à des multiples de la fréquence.",
            "Le filtre passe-bas les réduit à la source.",
            "On le place juste après l'émetteur-récepteur.",
          ],
          tip: "Un émetteur « propre » reste dans sa bande grâce au filtrage.",
        },
        {
          title: "Mesurer et régler l'antenne",
          body: "Pour juger l'**efficacité d'un système d'antenne**, on utilise un **ROS-mètre** (mesure du taux d'ondes stationnaires). Pour régler l'émetteur sans rayonner sur les ondes, on branche **temporairement** une **charge fictive** (dummy load), une résistance qui absorbe toute la puissance. Le **bloc d'accord d'antenne** adapte enfin l'impédance entre l'émetteur (50 Ω) et l'antenne.",
          points: [
            "ROS-mètre : évalue l'efficacité du système d'antenne.",
            "Charge fictive : raccordée durant les essais/réglages.",
            "Bloc d'accord : adapte l'impédance émetteur ↔ antenne.",
          ],
          tip: "On règle d'abord sur charge fictive, puis on bascule sur l'antenne.",
        },
        {
          title: "Sécurité électrique et mise à la terre",
          body: "L'électricité peut tuer. Une **bonne mise à la terre** relie les châssis à une terre commune et solide : elle protège des chocs et évacue les surtensions de foudre. **Coupez toujours l'alimentation** avant d'intervenir, et déconnectez les antennes lors d'un orage. Le conducteur **vert/jaune** est réservé à la terre.",
          points: [
            "Terre commune et solide pour tous les appareils.",
            "Coupez l'alimentation avant toute manipulation.",
            "Orage : déconnectez les antennes.",
          ],
          tip: "Le vert/jaune, c'est TOUJOURS la terre : ne l'utilisez à rien d'autre.",
        },
        {
          title: "Sécurité RF",
          body: "Les champs **radiofréquence (RF)** peuvent chauffer les tissus vivants (comme un four à micro-ondes, en beaucoup plus faible). On respecte des **distances de sécurité** avec les antennes, surtout à forte puissance, et on n'émet jamais avec la tête près d'une antenne active. Utiliser **juste la puissance nécessaire** est à la fois plus sûr et plus courtois.",
          points: [
            "Éloignez les antennes des zones occupées par des personnes.",
            "Plus la puissance est élevée, plus la distance de sécurité augmente.",
            "N'exposez jamais quelqu'un directement à une antenne en émission.",
          ],
          tip: "Bonne antenne + puissance raisonnable > grosse puissance mal maîtrisée.",
        },
        {
          title: "Fiche mémo — Station & sécurité",
          body: "Le montage type d'une station et les règles de sécurité vitales.",
          points: [
            "**Filtre passe-bas** au plus près de la sortie de l'émetteur (contre les **harmoniques**).",
            "**ROS-mètre** : évalue l'efficacité du système d'antenne.",
            "**Charge fictive** (dummy load) : pour régler sans rayonner sur les ondes.",
            "Conducteur **vert/jaune = terre**, jamais autre chose.",
            "**Coupez l'alimentation** avant toute intervention ; **orage → déconnectez les antennes**.",
            "Sécurité **RF** : distance de sécurité croissante avec la puissance.",
          ],
          tip: "Réglez d'abord sur charge fictive, puis basculez sur l'antenne.",
        },
      ],
    },
    "B-004": {
      tagline: "Reconnaître les composants actifs et leur rôle.",
      practiceIds: ["B-004-001-001", "B-004-001-007", "B-004-001-003", "B-004-002-001"],
      slides: [
        {
          title: "Amplificateur et gain",
          body: "Le composant vedette de cette section est l'**amplificateur** : un circuit qui **augmente le niveau de son signal d'entrée**. Cette augmentation s'appelle le **gain** (souvent exprimé en décibels). Un composant « à gain » (le transistor, par exemple) possède cette propriété, contrairement à une simple résistance qui ne fait qu'atténuer.",
          points: [
            "Amplificateur = augmente le niveau du signal d'entrée.",
            "Gain = la mesure de cette augmentation.",
            "Le transistor est l'élément amplificateur de base.",
          ],
          tip: "Amplifier = augmenter l'amplitude ; le gain la quantifie.",
        },
        {
          title: "RF ou audio ?",
          body: "Selon le signal traité, on parle d'**amplificateur RF** (pour un signal **radio très faible** capté par l'antenne) ou d'**amplificateur audio** (pour la voix issue d'un microphone). Le principe est le même : renforcer un signal faible. En réception, l'amplificateur RF d'entrée doit surtout ajouter **peu de bruit** pour ne pas noyer les signaux ténus.",
          points: [
            "Signal radio faible reçu par l'antenne → amplificateur RF.",
            "Signal vocal → amplificateur audio.",
            "En entrée de récepteur : privilégier le faible bruit.",
          ],
          tip: "« RF » = radiofréquence ; « AF » = audiofréquence.",
        },
        {
          title: "Diodes et régulation",
          body: "La **diode** ne laisse passer le courant que dans **un seul sens** : elle sert au **redressement** (transformer l'alternatif en continu) et à la **démodulation** (extraire l'information d'un signal). La **diode Zener**, branchée en inverse, maintient à ses bornes une **tension constante** : c'est son rôle de **régulateur de tension** dans les alimentations.",
          points: [
            "Diode : passage à sens unique (redressement, démodulation).",
            "Diode Zener : utilisée comme régulateur de tension.",
            "Une tension stable protège les circuits sensibles.",
          ],
          tip: "Zener ⇒ tension stabilisée.",
        },
        {
          title: "Fiche mémo — Composants",
          body: "Les composants actifs de base et leur fonction, à reconnaître d'un coup d'œil.",
          points: [
            "**Amplificateur** = augmente le niveau du signal ; le **gain** quantifie cette augmentation.",
            "**Transistor** = composant amplificateur de base.",
            "**Amplificateur RF** (signal radio faible) vs **audio** (voix du micro).",
            "**Diode** = passage à **sens unique** → **redressement** et **démodulation**.",
            "**Diode Zener** (en inverse) = **régulateur de tension** (tension constante).",
          ],
          tip: "Diode = 1 jonction, sens unique ; Zener = tension de référence stable.",
        },
      ],
    },
    "B-005": {
      tagline: "Maîtriser les unités, les conversions et les lois de base.",
      practiceIds: ["B-005-001-001", "B-005-001-006", "B-005-001-010", "B-005-001-005"],
      slides: [
        {
          title: "Les préfixes des unités",
          body: "En électronique, on jongle avec les préfixes pour éviter les longues suites de zéros : **kilo** (k, ×1000), **méga** (M, ×1 000 000), **milli** (m, ÷1000), **micro** (µ, ÷1 000 000). Ainsi **1 kilohm = 1000 ohms**, **1 mégahertz = 1000 kilohertz**, et **500 milliwatts = 0,5 watt**.",
          points: [
            "1 kΩ = 1000 Ω ; 1 MHz = 1000 kHz.",
            "milli = un millième (500 mW = 0,5 W).",
            "Monter d'un préfixe (k→M) = diviser le nombre par 1000.",
          ],
          tip: "Échelle des fréquences : Hz → kHz → MHz → GHz.",
        },
        {
          title: "Convertir les fréquences",
          body: "Une fréquence en **MHz** devient des **kHz** en multipliant par 1000. Un cadran affichant **3,525 MHz** indique donc **3 525 kHz** : c'est exactement la même fréquence, exprimée plus finement. Savoir passer d'une unité à l'autre est indispensable pour lire les plans de bande.",
          points: [
            "3,525 MHz = 3 525 kHz.",
            "De MHz vers kHz : × 1000.",
            "De W vers mW : × 1000.",
          ],
          tip: "Gardez toujours en tête l'unité affichée par votre appareil.",
        },
        {
          title: "Loi d'Ohm et puissance",
          body: "La relation fondamentale relie tension, courant et résistance : **U = R × I** (volts = ohms × ampères). La **puissance** dissipée vaut **P = U × I** (watts). Ces deux formules permettent de dimensionner une alimentation, choisir un fusible ou éviter de surchauffer un composant.",
          points: [
            "U = R × I (loi d'Ohm).",
            "P = U × I (puissance).",
            "À résistance fixe, doubler la tension double le courant.",
          ],
          tip: "Cachez la grandeur cherchée dans le triangle U / (R·I) pour retrouver la formule.",
        },
        {
          title: "Fréquence, longueur d'onde et dB",
          body: "Une onde a une **fréquence** (Hz) et une **longueur d'onde** (m), liées par : **λ(m) ≈ 300 / f(MHz)**. Plus la fréquence monte, plus la longueur d'onde raccourcit — d'où les tailles d'antennes. Le **décibel (dB)** exprime un rapport : **+3 dB** ≈ puissance **doublée**, **+10 dB** = ×10.",
          points: [
            "λ (m) = 300 / f (MHz).",
            "Haute fréquence ⇒ petite longueur d'onde ⇒ petite antenne.",
            "+3 dB ≈ ×2 en puissance ; +10 dB = ×10.",
          ],
          tip: "Le dB compare toujours deux valeurs : c'est un rapport, pas une quantité absolue.",
        },
        {
          title: "Fiche formules — Calculs essentiels",
          body: "Toutes les formules chiffrées de la section réunies. Apprenez-les : les calculs de l'examen s'y ramènent tous.",
          points: [
            "**Loi d'Ohm** : U = R × I. On en déduit R = U / I et I = U / R.",
            "**Puissance** : P = U × I = R × I² = U² / R (en watts).",
            "**Longueur d'onde** : λ (m) = 300 / f (MHz).",
            "**Préfixes** : k ×1000, M ×1 000 000, m ÷1000, µ ÷1 000 000.",
            "**Conversions** : 1 MHz = 1000 kHz ; 1 W = 1000 mW.",
            "**Décibels** : +3 dB ≈ ×2, −3 dB ≈ ÷2, +10 dB = ×10 en puissance.",
          ],
          tip: "Exemple : 12 V sur 8 Ω → I = 12/8 = 1,5 A et P = 12 × 1,5 = 18 W.",
        },
      ],
    },
    "B-006": {
      tagline: "Amener l'énergie jusqu'à l'antenne, avec le moins de pertes.",
      practiceIds: ["B-006-001-001", "B-006-002-001", "B-006-001-002", "B-006-001-005"],
      slides: [
        {
          title: "La ligne de transmission",
          body: "Ce qui **relie l'émetteur-récepteur à l'antenne** est une **ligne de transmission**. Son **impédance caractéristique** (souvent 50 Ω) est fixée par la **géométrie** du câble — diamètre et espacement des conducteurs — et **non par sa longueur**. Couper un bout de câble ne change donc pas son impédance caractéristique.",
          points: [
            "La ligne relie l'émetteur-récepteur à l'antenne.",
            "Impédance caractéristique = dépend des dimensions/positions des conducteurs.",
            "Elle ne dépend PAS de la longueur.",
          ],
          tip: "50 Ω est le standard des stations amateurs.",
        },
        {
          title: "Le câble coaxial",
          body: "Le **câble coaxial** est fait d'un **conducteur central**, entouré d'un **isolant**, lui-même entouré d'une **tresse conductrice** (le blindage). La tresse protège le signal des parasites extérieurs. Robuste, le coaxial peut même être **enfoui directement dans le sol** sans effet néfaste, ce qui est pratique pour une antenne éloignée.",
          points: [
            "Coaxial = âme centrale + isolant + tresse/blindage.",
            "La tresse protège des parasites.",
            "Le coaxial peut être enterré directement.",
          ],
          tip: "Un coax de qualité et court limite les pertes.",
          diagram: "coax",
        },
        {
          title: "TOS et antennes",
          body: "Le **Taux d'Ondes Stationnaires (TOS/ROS, ou SWR)** mesure la qualité de l'adaptation : **1:1** est idéal (toute la puissance part). Un TOS élevé signale de l'énergie **réfléchie**, donc des pertes. Le **dipôle demi-onde** rayonne perpendiculairement au fil ; l'**antenne verticale** rayonne autour d'elle. La **polarisation** (verticale/horizontale) doit être cohérente entre stations.",
          points: [
            "TOS 1:1 = idéal ; TOS élevé = pertes par réflexion.",
            "Dipôle : rayonne perpendiculairement au fil.",
            "Même polarisation des deux côtés pour un bon contact.",
          ],
          tip: "Vérifiez le TOS avant d'émettre à pleine puissance.",
          diagram: "dipole",
        },
        {
          title: "Fiche mémo — Lignes & antennes",
          body: "Ce qu'il faut retenir sur les lignes de transmission et les antennes de base.",
          points: [
            "**Impédance caractéristique** dépend de la **géométrie** du câble, **pas de sa longueur**.",
            "Standard des stations amateur : **50 Ω**.",
            "**Coaxial** = âme centrale + isolant + **tresse (blindage)** ; peut être enterré.",
            "**TOS 1:1** = idéal ; TOS élevé = énergie **réfléchie** = pertes.",
            "**Dipôle demi-onde** : rayonne **perpendiculairement** au fil.",
            "**Polarisation** (verticale/horizontale) identique des deux côtés du contact.",
          ],
          tip: "Un coax court et de qualité + un bon TOS = un maximum de puissance rayonnée.",
        },
      ],
    },
    "B-007": {
      tagline: "Comprendre pourquoi un signal voyage… ou pas.",
      practiceIds: ["B-007-001-001", "B-007-001-003", "B-007-001-006", "B-007-002-001"],
      slides: [
        {
          title: "Trois modes de propagation",
          body: "Une onde émise se propage de plusieurs façons. L'**onde de sol** longe la surface de la Terre (portée modeste, meilleures aux basses fréquences). L'**onde directe (à vue)** va en ligne droite d'une antenne à l'autre : c'est le mode dominant en **VHF/UHF**, par exemple entre deux portatifs proches. L'**onde de ciel** monte vers le ciel.",
          points: [
            "Onde de sol : suit la surface, courte portée.",
            "Onde directe : ligne de vue, dominante en VHF/UHF.",
            "Onde de ciel : part vers le haut (voir ci-après).",
          ],
          tip: "VHF/UHF ≈ portée optique ; HF ≈ rebonds à longue distance.",
          diagram: "propagation-modes",
        },
        {
          title: "L'ionosphère, ce miroir",
          body: "L'**ionosphère** est une région haute de l'atmosphère **ionisée par le rayonnement solaire** : elle réfléchit les ondes **HF** vers la Terre, permettant des liaisons de milliers de kilomètres (**onde de ciel** ou « skywave »). Comme c'est le Soleil qui l'entretient, ses propriétés changent entre le **jour et la nuit** et suivent le cycle solaire.",
          points: [
            "Ionosphère = couche ionisée par le Soleil.",
            "Elle réfléchit les HF vers le sol (grande portée).",
            "L'onde de ciel est redirigée vers la Terre par l'ionosphère.",
          ],
          tip: "Règle générale : bandes hautes le jour, bandes basses la nuit.",
          diagram: "ionosphere",
        },
        {
          title: "Portée en VHF/UHF",
          body: "En **VHF/UHF**, la propagation est essentiellement en **ligne de vue**, un peu au-delà de l'horizon optique. Les **obstacles** (immeubles, collines) bloquent le signal, et **plus l'antenne est haute**, plus la portée augmente. C'est pourquoi les répéteurs sont installés en hauteur : ils « voient » loin dans toutes les directions.",
          points: [
            "VHF/UHF : portée directe, sensible aux obstacles.",
            "Antenne plus haute = portée plus grande.",
            "Les répéteurs exploitent la hauteur pour couvrir une large zone.",
          ],
          tip: "Gagner en hauteur d'antenne vaut souvent mieux que gagner en puissance.",
          diagram: "vhf-los",
        },
        {
          title: "Fiche mémo — Propagation",
          body: "Les modes de propagation et les règles jour/nuit à maîtriser.",
          points: [
            "**Onde de sol** : suit la surface, courte portée, meilleure en basses fréquences.",
            "**Onde directe (à vue)** : dominante en **VHF/UHF**.",
            "**Onde de ciel** : réfléchie par l'**ionosphère** (liaisons HF de milliers de km).",
            "**Ionosphère** = couche ionisée par le **Soleil** (varie jour/nuit et cycle solaire).",
            "Règle : **bandes hautes le jour**, **bandes basses la nuit**.",
            "VHF/UHF : **antenne plus haute = portée plus grande** ; obstacles bloquants.",
          ],
          tip: "HF = rebonds longue distance via l'ionosphère ; VHF/UHF = portée quasi optique.",
        },
      ],
    },
    "B-008": {
      tagline: "Diagnostiquer et réduire les interférences en bon voisin.",
      practiceIds: ["B-008-001-001", "B-008-001-003", "B-008-001-004", "B-008-002-001"],
      slides: [
        {
          title: "La surcharge du récepteur",
          body: "La **surcharge (overload)** est un brouillage **causé par un émetteur puissant situé à proximité** : l'étage d'entrée de l'appareil victime sature. Signe caractéristique : le brouillage d'un téléviseur apparaît **quelle que soit la bande** que vous utilisez. Ce n'est pas forcément que votre signal est « sale » — c'est souvent l'appareil voisin qui manque de sélectivité.",
          points: [
            "Surcharge = signal fort tout proche qui sature le récepteur.",
            "Brouillage TV sur toutes les bandes ⇒ surcharge.",
            "La cause peut être le manque de sélectivité de l'appareil victime.",
          ],
          tip: "Même symptôme quelle que soit votre fréquence = surcharge.",
        },
        {
          title: "Harmoniques et bande passante",
          body: "À l'inverse de la surcharge, un émetteur mal filtré peut rayonner des **harmoniques** hors de sa bande. On les combat côté émetteur avec un **filtre passe-bas** (revoir B-003). Il faut aussi rester dans la **largeur de bande** autorisée : émettre trop large brouille les voisins de fréquence.",
          points: [
            "Harmoniques : filtre passe-bas côté émetteur.",
            "Restez dans la largeur de bande de votre mode.",
            "Un signal net respecte ses voisins de fréquence.",
          ],
          tip: "Filtrez à la source : c'est votre responsabilité d'émetteur.",
        },
        {
          title: "Filtres et ferrites",
          body: "Contre la surcharge d'un **téléviseur** par un émetteur HF, on installe un **filtre passe-haut à l'entrée d'antenne du téléviseur** : il laisse passer les hautes fréquences TV et bloque votre signal HF plus bas. Contre le **brouillage audio** des appareils domestiques (chaîne, téléphone), on enfile des **bobines sur noyau de ferrite** autour des câbles.",
          points: [
            "Filtre passe-haut sur le téléviseur : contre la surcharge HF.",
            "Ferrites sur les câbles : contre le brouillage audio domestique.",
            "Vérifiez d'abord votre propre station.",
          ],
          tip: "Bon voisinage : coopérez pour localiser la source du brouillage.",
        },
        {
          title: "Fiche mémo — Interférences",
          body: "Diagnostiquer et corriger un brouillage, chez vous comme chez le voisin.",
          points: [
            "**Surcharge** : émetteur fort tout proche qui sature le récepteur → brouillage **sur toutes les bandes**.",
            "**Harmoniques** : émetteur mal filtré → **filtre passe-bas** côté émetteur.",
            "**Filtre passe-haut** à l'entrée d'antenne du **téléviseur** (contre la surcharge HF).",
            "**Ferrites** enfilées sur les câbles : contre le brouillage **audio** domestique.",
            "Restez toujours dans la **largeur de bande** autorisée de votre mode.",
          ],
          tip: "Symptôme identique quelle que soit VOTRE fréquence = surcharge du voisin.",
        },
      ],
    },
  },
  advanced: {
    "A-001": {
      tagline: "La théorie des circuits RC et RL, réactance et résonance.",
      practiceIds: ["A-001-001-001", "A-001-001-002", "A-001-001-004", "A-001-001-008"],
      slides: [
        {
          title: "La constante de temps",
          body: "Quand on charge un condensateur à travers une résistance (**RC**), la tension monte vite puis ralentit. La **constante de temps** (τ) est le temps pour atteindre **63,2 %** de la valeur finale. Pour un circuit **RL**, c'est le temps que met le **courant** dans la bobine à atteindre 63,2 % de son maximum. À la **décharge**, après une constante de temps, il reste **36,8 %**.",
          points: [
            "RL : courant à 63,2 % de sa valeur max après 1 τ.",
            "RC : condensateur chargé à 63,2 % après 1 τ.",
            "Décharge : il reste 36,8 % après 1 τ.",
          ],
          tip: "63,2 % à la charge, 36,8 % à la décharge : les deux chiffres clés.",
          diagram: "time-constant",
        },
        {
          title: "Calculer τ",
          body: "Pour un circuit **RC** : **τ = R × C** (ohms × farads = secondes). Exemple : **100 µF** avec **470 kΩ** → 470 000 × 0,0001 = **47 secondes**. Pour un circuit **RL** : **τ = L / R**. Après environ **5 τ**, on considère le régime établi (>99 %).",
          points: [
            "RC : τ = R × C. RL : τ = L / R.",
            "100 µF × 470 kΩ = 47 s.",
            "Régime établi après ≈ 5 τ.",
          ],
          tip: "Vérifiez les unités : farads × ohms donnent bien des secondes.",
        },
        {
          title: "Réactance",
          body: "En alternatif, un condensateur et une bobine s'opposent au courant sans le dissiper : c'est la **réactance**. La réactance **capacitive Xc** **diminue** quand la fréquence monte (le condensateur « laisse mieux passer » l'alternatif rapide). La réactance **inductive Xl** **augmente** avec la fréquence (la bobine s'oppose davantage aux variations rapides).",
          points: [
            "Xc diminue quand la fréquence augmente.",
            "Xl augmente quand la fréquence augmente.",
            "La réactance stocke/restitue l'énergie (ne la dissipe pas).",
          ],
          tip: "Condensateur = passe l'alternatif rapide ; bobine = le freine.",
        },
        {
          title: "Résonance et facteur Q",
          body: "Un circuit LC est en **résonance** lorsque **Xl = Xc** : l'impédance devient purement résistive et le circuit « sélectionne » une fréquence précise. Le **facteur Q** décrit la finesse de cette sélection : un **Q élevé** donne une **bande passante étroite** (grande sélectivité), un Q faible une bande large.",
          points: [
            "Résonance : Xl = Xc, impédance résistive.",
            "Q élevé ⇒ bande étroite ⇒ forte sélectivité.",
            "C'est la base des circuits accordés et des filtres.",
          ],
          tip: "Un circuit accordé « choisit » sa fréquence : c'est la résonance.",
          diagram: "resonance",
        },
        {
          title: "Fiche formules — RC, RL & résonance",
          body: "Les formules chiffrées de la section, prêtes à l'emploi pour l'examen supérieur.",
          points: [
            "**Constante de temps** : τ = R × C (circuit RC) ; τ = L / R (circuit RL).",
            "Après **1 τ** : **63,2 %** à la charge, **36,8 %** restants à la décharge ; régime établi ≈ **5 τ**.",
            "**Réactance capacitive** : Xc = 1 / (2πfC) → **diminue** quand f augmente.",
            "**Réactance inductive** : Xl = 2πfL → **augmente** quand f augmente.",
            "**Résonance** : Xl = Xc, impédance résistive ; f = 1 / (2π√(LC)).",
            "**Facteur Q** élevé ⇒ **bande passante étroite** ⇒ forte sélectivité.",
          ],
          tip: "Exemple : 470 kΩ × 100 µF = 470 000 × 0,0001 = 47 s.",
        },
      ],
    },
    "A-002": {
      tagline: "Les semi-conducteurs, du matériau au composant.",
      practiceIds: ["A-002-001-001", "A-002-001-008", "A-002-001-003", "A-002-001-005"],
      slides: [
        {
          title: "Qu'est-ce qu'un semi-conducteur ?",
          body: "Un **semi-conducteur** est un matériau à mi-chemin : ni bon conducteur (comme le cuivre), ni bon isolant (comme le verre). Selon les conditions, il peut se comporter comme **l'un ou l'autre**. Les deux éléments les plus employés, à caractéristiques à la fois métalliques et non métalliques, sont le **silicium** et le **germanium**. Le silicium pur, seul, est plutôt isolant.",
          points: [
            "Semi-conducteur = tantôt isolant, tantôt conducteur.",
            "Éléments de base : silicium et germanium.",
            "Le silicium pur est plutôt isolant.",
          ],
          tip: "Silicium + germanium : le duo fondateur de l'électronique.",
        },
        {
          title: "Le dopage : types P et N",
          body: "En ajoutant des impuretés (**dopage**), on crée deux types. Le **type N** a un **excès d'électrons libres** (charges négatives) : ses porteurs majoritaires sont les **électrons**. Le **type P** a **moins d'électrons libres** que le cristal pur, créant des « trous » (places vides) : ses porteurs majoritaires sont les **trous**, qui se comportent comme des charges positives.",
          points: [
            "Type N : excès d'électrons (porteurs = électrons).",
            "Type P : moins d'électrons libres (porteurs = trous).",
            "Trou = absence d'électron, se comporte comme + .",
          ],
          tip: "N comme Négatif/électrons, P comme Positif/trous.",
        },
        {
          title: "La jonction P-N",
          body: "En accolant une zone **P** et une zone **N**, on obtient une **jonction P-N** : c'est la base de la **diode**. Elle conduit dans un sens (polarisation directe) et bloque dans l'autre (polarisation inverse). En empilant trois zones (NPN ou PNP), on obtient un **transistor bipolaire**, capable d'amplifier ou de commuter.",
          points: [
            "Jonction P-N = brique de la diode.",
            "Conduit en direct, bloque en inverse.",
            "NPN/PNP = transistor (amplification, commutation).",
          ],
          tip: "Diode = 1 jonction ; transistor bipolaire = 2 jonctions.",
          diagram: "pn-junction",
        },
        {
          title: "Fiche mémo — Semi-conducteurs",
          body: "Du matériau au composant : la logique des jonctions à retenir.",
          points: [
            "**Semi-conducteur** = ni bon conducteur ni bon isolant ; éléments : **silicium** et **germanium**.",
            "**Type N** : excès d'**électrons libres** (porteurs = électrons, charges −).",
            "**Type P** : moins d'électrons → **trous** (porteurs = trous, charges +).",
            "**Jonction P-N** = **diode** : conduit en direct, bloque en inverse.",
            "**Transistor bipolaire** = 3 zones **NPN** ou **PNP** (2 jonctions) → amplification/commutation.",
          ],
          tip: "N comme Négatif (électrons), P comme Positif (trous).",
          diagram: "transistor",
        },
      ],
    },
    "A-003": {
      tagline: "Mesurer correctement les signaux alternatifs.",
      practiceIds: ["A-003-001-001", "A-003-001-007", "A-003-001-006", "A-003-001-003"],
      slides: [
        {
          title: "L'oscilloscope",
          body: "L'**oscilloscope** affiche la tension en fonction du temps : on « voit » la forme d'onde. Sur une sinusoïde pure, la valeur la plus **facile et directe** à mesurer est l'amplitude **crête à crête** (du sommet positif au sommet négatif). On en déduit la **crête** (la moitié) puis la **valeur efficace**.",
          points: [
            "Crête à crête = du max positif au max négatif.",
            "C'est la mesure la plus directe à l'écran.",
            "Crête à crête = 2 × crête.",
          ],
          tip: "Sur une sinusoïde, tout se déduit de la mesure crête à crête.",
        },
        {
          title: "La valeur efficace (RMS)",
          body: "La **valeur efficace (RMS)** d'une tension alternative est celle qui **dissiperait la même chaleur dans une résistance qu'une tension continue** de même valeur. Pour une sinusoïde, **RMS = 0,707 × crête** (70,7 %). Les **voltmètres CA** sont habituellement **étalonnés en RMS** : la valeur lue est déjà l'équivalent continu.",
          points: [
            "RMS ≡ même échauffement qu'un continu équivalent.",
            "RMS = 0,707 × valeur de crête.",
            "Les voltmètres CA affichent la RMS.",
          ],
          tip: "0,707 : le facteur magique crête → RMS.",
        },
        {
          title: "Les autres instruments",
          body: "Le **multimètre** mesure tension, courant et résistance — attention au bon calibre et au bon mode. Le **fréquencemètre** donne la fréquence avec précision, le **ROS-mètre** l'adaptation d'antenne, et le **grid-dip mètre** trouve la **fréquence de résonance** d'un circuit accordé.",
          points: [
            "Multimètre : U, I, R (bon calibre/mode).",
            "Fréquencemètre : mesure de fréquence précise.",
            "Dip-mètre : détecte la résonance d'un circuit.",
          ],
          tip: "Chaque grandeur a son instrument : choisissez le bon outil.",
        },
        {
          title: "Fiche formules — Mesures CA",
          body: "Les conversions entre valeurs d'une sinusoïde et les instruments associés.",
          points: [
            "**Crête à crête** = 2 × **crête** (la mesure la plus directe à l'oscilloscope).",
            "**RMS** = 0,707 × crête ; **crête** = 1,414 × RMS.",
            "**RMS** ≡ même **échauffement** dans une résistance qu'un continu équivalent.",
            "Les **voltmètres CA** sont étalonnés en **RMS**.",
            "Instruments : **multimètre** (U, I, R), **fréquencemètre** (f), **dip-mètre** (résonance).",
          ],
          tip: "Exemple : 100 V crête → RMS = 70,7 V ; crête à crête = 200 V.",
        },
      ],
    },
    "A-004": {
      tagline: "Fournir une alimentation continue, propre et stable.",
      practiceIds: ["A-004-001-001", "A-004-001-005", "A-004-001-006", "A-004-001-007"],
      slides: [
        {
          title: "La chaîne d'alimentation",
          body: "Un **bloc d'alimentation** transforme l'alternatif du secteur en continu utilisable, en quatre étapes : **transformateur** (change la tension) → **redresseur** (rend le courant unidirectionnel) → **filtrage** (lisse) → **régulation** (stabilise). Chaque étage a un rôle précis.",
          points: [
            "Transformateur : adapte la tension.",
            "Redresseur : rend le courant unidirectionnel.",
            "Filtrage puis régulation : lissent et stabilisent.",
          ],
          tip: "Transformer → redresser → filtrer → réguler.",
        },
        {
          title: "Redresseur en pont",
          body: "Le **redresseur en pont** (4 diodes) exploite **les deux alternances** du cycle sans prise médiane. Pour une même tension au secondaire, il fournit la **tension moyenne la plus élevée** — environ le **double** d'un montage double alternance à prise médiane. C'est le choix le plus courant.",
          points: [
            "Pont = 4 diodes, exploite les deux alternances.",
            "Tension moyenne la plus élevée pour un secondaire donné.",
            "≈ le double d'un montage à prise médiane.",
          ],
          tip: "Besoin de la tension la plus haute ? Pensez « pont ».",
          diagram: "bridge-rectifier",
        },
        {
          title: "L'ondulation (ripple)",
          body: "Après redressement, il subsiste une **ondulation**. Sur secteur 60 Hz, un montage **simple alternance** donne une ondulation à **60 Hz**, un montage **double alternance** à **120 Hz** (deux fois plus élevée). Une ondulation à **fréquence plus haute est plus facile à filtrer** avec un condensateur. Le **régulateur** final maintient ensuite une sortie constante malgré la charge.",
          points: [
            "Simple alternance : ondulation à 60 Hz.",
            "Double alternance : ondulation à 120 Hz.",
            "Fréquence plus haute ⇒ filtrage plus facile.",
          ],
          tip: "Double alternance = fréquence d'ondulation doublée.",
        },
        {
          title: "Fiche mémo — Alimentations",
          body: "La chaîne d'alimentation et les chiffres d'ondulation à connaître.",
          points: [
            "Chaîne : **transformateur** → **redresseur** → **filtrage** → **régulation**.",
            "**Redresseur en pont** = 4 diodes, exploite **les deux alternances**, tension moyenne la **plus élevée**.",
            "Ondulation : **simple alternance = 60 Hz**, **double alternance / pont = 120 Hz** (secteur 60 Hz).",
            "Ondulation à **fréquence plus haute** = **plus facile à filtrer**.",
            "Le **régulateur** maintient la sortie constante malgré la variation de charge.",
          ],
          tip: "Besoin de la tension la plus haute pour un secondaire donné ? Le pont.",
        },
      ],
    },
    "A-005": {
      tagline: "Produire et façonner le signal à émettre.",
      practiceIds: ["A-005-001-001", "A-005-001-002", "A-005-001-006", "A-005-001-005"],
      slides: [
        {
          title: "Hartley et Colpitts",
          body: "Un **oscillateur** entretient une oscillation grâce à une **rétroaction positive** (une partie de la sortie revient en phase à l'entrée). Ce qui distingue les modèles, c'est **comment** on prélève cette fraction. **Hartley** : à travers une **bobine à prise** (division inductive). **Colpitts** : à travers un **diviseur capacitif** (deux condensateurs).",
          points: [
            "Oscillateur = ampli + rétroaction positive + circuit accordé.",
            "Hartley : rétroaction par bobine à prise (inductif).",
            "Colpitts : rétroaction par diviseur capacitif.",
          ],
          tip: "Diviseur capacitif ⇒ Colpitts ; bobine à prise ⇒ Hartley.",
        },
        {
          title: "Stabilité et PLL",
          body: "Un bon oscillateur doit être **stable** en fréquence : le **quartz** apporte cette stabilité. Un synthétiseur **PLL (boucle à phase asservie)** verrouille sa sortie sur un **oscillateur de référence très stable** ; toute **variation de phase** de la référence se traduit par du **bruit de phase** en sortie.",
          points: [
            "Le quartz stabilise fortement la fréquence.",
            "La PLL exige une référence très stable.",
            "Variation de phase de la référence → bruit de phase.",
          ],
          tip: "Référence instable = sortie bruitée : la stabilité est reine.",
        },
        {
          title: "Modulation et mélange",
          body: "Pour transporter l'information, on **module** une porteuse : **AM** (amplitude), **FM** (fréquence) ou **BLU/SSB** (bande latérale unique, très efficace en puissance et en occupation spectrale). Un **mélangeur** combine deux fréquences et produit leur **somme et leur différence**, ce qui permet de translater un signal vers une autre fréquence.",
          points: [
            "AM, FM, SSB : différentes façons de moduler la porteuse.",
            "SSB : reine de la voix HF longue distance.",
            "Mélangeur : produit somme et différence de fréquences.",
          ],
          tip: "La BLU (SSB) concentre l'énergie dans le signal utile.",
        },
        {
          title: "Fiche mémo — Oscillateurs & modulation",
          body: "Reconnaître les oscillateurs et les modes de modulation.",
          points: [
            "**Oscillateur** = amplificateur + **rétroaction positive** + circuit accordé.",
            "**Hartley** : rétroaction par **bobine à prise** (division inductive).",
            "**Colpitts** : rétroaction par **diviseur capacitif** (deux condensateurs).",
            "**Quartz** = grande **stabilité** ; **PLL** = verrouillée sur une référence stable (sinon **bruit de phase**).",
            "Modulation : **AM** (amplitude), **FM** (fréquence), **SSB/BLU** (bande latérale unique, la plus efficace).",
            "**Mélangeur** : produit la **somme** et la **différence** des deux fréquences.",
          ],
          tip: "Bobine à prise ⇒ Hartley ; diviseur capacitif ⇒ Colpitts.",
        },
      ],
    },
    "A-006": {
      tagline: "Extraire un signal faible au milieu du bruit.",
      practiceIds: ["A-006-001-001", "A-006-001-004", "A-006-001-007", "A-006-001-008"],
      slides: [
        {
          title: "Le superhétérodyne",
          body: "Le **superhétérodyne** convertit le signal reçu vers une **fréquence intermédiaire (FI) fixe**. Avantages : une **meilleure sélectivité** et des **circuits résonants optimaux**, car on optimise le filtrage une seule fois, à la FI, quelle que soit la station écoutée. Le **filtre à cristal**, très sélectif, se situe dans les **circuits FI**.",
          points: [
            "Conversion vers une FI fixe = filtrage optimal.",
            "Avantage clé : meilleure sélectivité.",
            "Le filtre à cristal est dans les circuits FI.",
          ],
          tip: "FI fixe = filtres optimisés une fois pour toutes.",
          diagram: "superhet",
        },
        {
          title: "Les étages, du signal à l'audio",
          body: "L'**amplificateur RF** est l'étage d'entrée : entrée ET sortie **accordées sur la fréquence reçue**, pour amplifier proprement le faible signal capté. Le **mélangeur** se place **entre l'étage accordable et l'étage à fréquence fixe** : combiné à l'**oscillateur local**, il produit la FI. Suivent l'amplification FI, la détection puis l'audio.",
          points: [
            "Amplificateur RF : entrée/sortie accordées sur la fréquence reçue.",
            "Mélangeur : entre l'étage accordable et l'étage fixe.",
            "Oscillateur local + mélangeur = création de la FI.",
          ],
          tip: "Le mélangeur « traduit » le signal reçu vers la FI.",
        },
        {
          title: "Sélectivité, sensibilité, AGC",
          body: "La **sélectivité** est l'aptitude à séparer deux stations proches ; la **sensibilité**, à capter un signal très faible. La **commande automatique de gain (AGC/CAG)** ajuste le gain en continu : elle **réduit** le gain sur les signaux forts et l'**augmente** sur les faibles, pour un volume stable et sans saturation.",
          points: [
            "Sélectivité : séparer deux signaux voisins.",
            "Sensibilité : capter les signaux ténus.",
            "AGC : stabilise le niveau, évite la saturation.",
          ],
          tip: "Sans AGC, un signal fort « écraserait » vos oreilles.",
        },
        {
          title: "Fiche mémo — Récepteurs",
          body: "L'architecture superhétérodyne et ses étages clés.",
          points: [
            "**Superhétérodyne** : conversion vers une **fréquence intermédiaire (FI) fixe** → **meilleure sélectivité**.",
            "Le **filtre à cristal** (très sélectif) se trouve dans les **circuits FI**.",
            "**Amplificateur RF** d'entrée : entrée ET sortie **accordées sur la fréquence reçue**.",
            "**Mélangeur** + **oscillateur local** = création de la FI.",
            "**Sélectivité** = séparer deux stations proches ; **sensibilité** = capter un signal faible.",
            "**AGC/CAG** : réduit le gain sur les signaux forts, l'augmente sur les faibles.",
          ],
          tip: "Le mélangeur « traduit » le signal reçu vers la FI, où l'on filtre une fois pour toutes.",
        },
      ],
    },
    "A-007": {
      tagline: "Adapter l'impédance et rayonner avec précision.",
      practiceIds: ["A-007-001-005", "A-007-001-006", "A-007-001-007", "A-007-001-008"],
      slides: [
        {
          title: "Pourquoi adapter ?",
          body: "L'étage final d'un émetteur travaille au mieux sur une charge de **50 Ω**. Or une antenne réelle présente rarement exactement 50 Ω. Un **adaptateur d'impédance (coupleur)** présente à l'émetteur une charge proche de 50 Ω : cela **minimise le TOS**, protège l'étage final et maximise le transfert de puissance. Il n'améliore pas l'antenne : il adapte la charge vue par l'émetteur.",
          points: [
            "L'étage final aime voir 50 Ω.",
            "Le coupleur minimise le TOS et protège l'émetteur.",
            "Il adapte la charge, il ne « répare » pas l'antenne.",
          ],
          tip: "Adapter ≠ améliorer l'antenne : cela protège surtout l'émetteur.",
        },
        {
          title: "Réseaux en pi et en L",
          body: "Un **adaptateur en pi** est composé d'**un condensateur et de deux bobines, ou d'une bobine et de deux condensateurs** (sa forme rappelle π). C'est celui qui offre la **plage d'adaptation la plus étendue**. À l'inverse, l'**adaptateur en L**, plus simple, ne couvre qu'une **gamme d'impédance limitée**. Le réseau en **T** est un autre compromis courant.",
          points: [
            "Pi : 1 condensateur + 2 bobines (ou l'inverse).",
            "Le pi offre la plage d'adaptation la plus large.",
            "Le L est plus simple mais à plage limitée.",
          ],
          tip: "Besoin d'une large plage ? L'adaptateur en pi.",
          diagram: "pi-l-network",
        },
        {
          title: "Comment agit un adaptateur",
          body: "Une impédance a deux parties : une partie **réactive** (bobines/condensateurs, qui déphase le courant) et une partie **résistive**. L'adaptateur procède en deux temps : il **annule d'abord la composante réactive**, puis **transforme la partie résistive** jusqu'à 50 Ω. L'émetteur « voit » alors sa charge idéale.",
          points: [
            "1) Annuler la composante réactive.",
            "2) Transformer la partie résistive vers 50 Ω.",
            "Résultat : TOS bas et étage final protégé.",
          ],
          tip: "Réactance annulée, résistance transformée : voilà l'adaptation.",
        },
        {
          title: "Fiche mémo — Adaptation d'impédance",
          body: "Pourquoi et comment adapter l'antenne à l'émetteur.",
          points: [
            "L'**étage final** travaille au mieux sur **50 Ω**.",
            "Le **coupleur** présente ≈ 50 Ω à l'émetteur : **minimise le TOS** et **protège l'étage final**.",
            "Il **adapte la charge vue**, il **n'améliore pas** l'antenne elle-même.",
            "**Réseau en pi** (1 C + 2 L, ou l'inverse) : **plage d'adaptation la plus large**.",
            "**Réseau en L** : plus simple mais **plage limitée** ; réseau en **T** = compromis.",
            "Méthode : **1) annuler la réactance**, **2) transformer la résistance** vers 50 Ω.",
          ],
          tip: "Besoin d'une large plage d'adaptation ? L'adaptateur en pi.",
        },
      ],
    },
  },
};
